import React, { useState } from 'react';
import { approvals } from '../data/store';

// ── ApprovalRequestModal ───────────────────────────────────────────────────────
// Shows when a user tries to edit an old record — lets them submit for admin approval
export function ApprovalRequestModal({ sample, changeType, description,
  proposedChanges, currentValues, session, onSubmitted, onCancel }) {

  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    approvals.request({
      sampleId: sample?.id,
      requestedBy: session.userId,
      requestedByName: session.displayName,
      changeType,
      description,
      proposedChanges,
      currentValues,
      note,
    });
    setSubmitted(true);
    setTimeout(() => { onSubmitted && onSubmitted(); }, 1500);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{width:520}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header" style={{background:'#e09900'}}>
          <h2>🔒 Admin Approval Required</h2>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          {submitted ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:15,fontWeight:700,color:'#1a6b42',marginBottom:6}}>
                Request Submitted
              </div>
              <div style={{fontSize:12,color:'#666'}}>
                An admin will review and approve your change request.
              </div>
            </div>
          ) : (
            <>
              <div style={{background:'#fff8e8',border:'1px solid #e0c060',borderRadius:6,
                padding:'10px 14px',marginBottom:14,fontSize:12}}>
                <strong style={{color:'#7a5a00'}}>⚠️ The 24-hour editing window has expired.</strong>
                <div style={{color:'#666',marginTop:4}}>
                  Changes more than 24 hours after sample creation or receipt require admin approval.
                  Submit your request below and an admin will review it.
                </div>
              </div>

              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:'#555',
                  textTransform:'uppercase',marginBottom:4}}>Requested Change</div>
                <div style={{background:'#f8f9ff',border:'1px solid #e0e4ec',borderRadius:4,
                  padding:'8px 12px',fontSize:12,color:'#333'}}>
                  {description}
                </div>
              </div>

              {proposedChanges && Object.keys(proposedChanges).length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#555',
                    textTransform:'uppercase',marginBottom:4}}>Proposed Values</div>
                  <div style={{background:'#f0f6ff',border:'1px solid #aac8ee',borderRadius:4,
                    padding:'8px 12px',fontSize:11}}>
                    {Object.entries(proposedChanges).map(([k,v])=>(
                      <div key={k} style={{display:'flex',gap:8,marginBottom:3}}>
                        <span style={{color:'#888',minWidth:100}}>{k}:</span>
                        <span style={{color:'#1e3060',fontWeight:600}}>{String(v)}</span>
                        {currentValues?.[k] !== undefined && (
                          <span style={{color:'#aaa',fontSize:10}}>
                            (was: {String(currentValues[k])})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Reason for Change (optional)</label>
                <textarea value={note} onChange={e=>setNote(e.target.value)}
                  rows={3} placeholder="Explain why this change is needed..."
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',
                    fontSize:12,width:'100%',resize:'vertical',fontFamily:'inherit',outline:'none'}}/>
              </div>
            </>
          )}
        </div>
        {!submitted && (
          <div className="modal-footer">
            <button className="btn"
              style={{background:'#e09900',borderColor:'#e09900',color:'white',fontWeight:700}}
              onClick={handleSubmit}>
              📋 Submit for Admin Approval
            </button>
            <button className="btn" onClick={onCancel}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── useApprovalCheck hook ─────────────────────────────────────────────────────
// Returns a function that checks if an edit is allowed.
// If admin → always allowed.
// If the sample is within its first 24 hours → allowed.
// Otherwise → show approval modal.
export function useApprovalCheck(session) {
  const [pendingAction, setPendingAction] = useState(null);

  const check = ({ sampleId, changeType, description, proposedChanges,
    currentValues, sample, onAllowed }) => {
    // Admins can always edit
    if (session?.role === 'admin') { onAllowed(); return; }
    // Within the 24-hour edit window → allow
    if (approvals.canEditFreely(sampleId)) { onAllowed(); return; }
    // Otherwise → show approval modal
    setPendingAction({ sampleId, changeType, description,
      proposedChanges, currentValues, sample, onAllowed });
  };

  const modal = pendingAction ? (
    <ApprovalRequestModal
      sample={pendingAction.sample}
      changeType={pendingAction.changeType}
      description={pendingAction.description}
      proposedChanges={pendingAction.proposedChanges}
      currentValues={pendingAction.currentValues}
      session={session}
      onSubmitted={() => setPendingAction(null)}
      onCancel={() => setPendingAction(null)}
    />
  ) : null;

  return { check, modal };
}
